/**
 * The portal's crash screen — what a route shows when its screen throws while rendering.
 *
 * TanStack's built-in fallback is an English "Something went wrong!" with a *Hide Error* button
 * over the raw exception message: untranslated on a portal that is bilingual by rule, rendered
 * outside `AppShell`, and a developer's message handed to a traffic manager. ASGTRANS was shown
 * exactly that on 2026-09-22, when *Crear clave* crashed with "Cannot read properties of null
 * (reading 'checked')" (#105).
 *
 * **The exception's text is never rendered.** It is not lost: React reports every error a boundary
 * catches to `console.error` — `createRoot`'s default `onCaughtError`, which `main.tsx` leaves in
 * place — and the portal has no reporter to send it to instead. Sentry is wired into the backend
 * and the Flutter app, not here, and a Sentry that sees DeCA data is gated behind a DPA notice
 * rather than switched on by default (architecture spec §19.7).
 *
 * `EmptyState` inside an alert, not `ErrorState`: `ErrorState` prints `error.message`, the one thing
 * this screen must not do. The alert role is the one `NotFoundScreen` leaves out on purpose — being
 * lost is not an incident, but a screen that crashed under the user is, and a screen reader should
 * say so unprompted.
 *
 * *Reintentar* calls the boundary's `reset`, which renders the failed screen again. Navigating away
 * clears the boundary as well: TanStack keys every route boundary on the router's `loadedAt`.
 *
 * ⚠️ **This replaces whichever route threw, so it sits behind the auth gate only when that route
 * did** — `/login`, `/restablecer`, the root layout and `AuthenticatedLayout` itself are outside it.
 * The three states are therefore handled here, as in `NotFoundScreen`, with one deliberate
 * difference:
 *
 *  - `restoring` — the progress bar, until the silent refresh has decided which layout applies.
 *  - `anonymous` — the card on its own, and **not** `NotFoundScreen`'s redirect to `/login`. When
 *    `/login` is the screen that threw, navigating to the URL already showing reloads it, the reload
 *    resets the boundary, the screen throws again and the redirect fires again — a loop, which a
 *    throwaway probe counted at 119 crashes in 1.5 s. On `/restablecer` a redirect would also drop
 *    the token the link carries.
 *  - signed in — the card inside the shell, so the navigation is still there to leave by.
 */

import { Component, type ReactNode } from 'react'
import { useNavigate, type ErrorComponentProps } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { useAuth } from '../app/auth.tsx'
import { landingPathFor } from '../deca/navigation.ts'
import { AppShell } from '../shell/AppShell.tsx'
import { Button, Card, EmptyState, ProgressBar } from '../ui/primitives.tsx'

export function ErrorScreen({ reset }: ErrorComponentProps) {
  const { t } = useTranslation()
  const { status, principal } = useAuth()
  const navigate = useNavigate()

  if (status === 'restoring') {
    return (
      <div className="auth-screen">
        <div style={{ width: 240 }}>
          <ProgressBar />
        </div>
      </div>
    )
  }

  // Where this role's portal begins, as on the 404: a platform role's Panel would bounce it.
  const home = landingPathFor(principal)

  const card = (
    <Card>
      <div role="alert">
        <EmptyState
          icon="alert"
          title={t('errorScreen.title')}
          body={t('errorScreen.body')}
          action={
            // Wraps, centred, where the two do not fit on one line: a phone, or longer labels.
            <div className="row wrap error-screen__actions">
              <Button icon="refresh" onClick={reset}>
                {t('common.retry')}
              </Button>
              <Button variant="primary" onClick={() => void navigate({ to: home })}>
                {t('errorScreen.action')}
              </Button>
            </div>
          }
        />
      </div>
    </Card>
  )

  // Without the shell — signed out, or the shell is what failed: the sign-in screen's white side,
  // stretched to the page.
  const standalone = (
    <div className="auth-form-side" style={{ minHeight: '100%' }}>
      <div className="auth-card">{card}</div>
    </div>
  )

  if (status === 'anonymous') return standalone

  return (
    <ShellBoundary fallback={standalone}>
      <AppShell title={t('errorScreen.title')}>{card}</AppShell>
    </ShellBoundary>
  )
}

/**
 * The shell's own boundary, falling back to the card on its own.
 *
 * Every screen renders `AppShell`, so a defect in the shell crashes this screen as well. Without
 * this the error climbs every route's boundary, failing again at each, up to the one TanStack wraps
 * around the whole tree — whose component is not configurable, so the user would be back on the
 * built-in English screen.
 *
 * A class because React has no hook that catches render errors. Not TanStack's `CatchBoundary`:
 * that takes its fallback as a component type, and this fallback has to close over the route's
 * `reset` — a component declared inline would be a new type on every render, and remount.
 */
class ShellBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { failed: boolean }> {
  override state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  override render() {
    return this.state.failed ? this.props.fallback : this.props.children
  }
}
